import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, Mail, Phone, ShieldCheck, Ban, CheckCircle, AlertCircle,
  Loader2, Award, ShoppingBag, Calendar, Globe,
} from 'lucide-react';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { OrderStatusPill } from '@/components/shared/StatusPill';
import { adminApi } from '@/services/api';
import { formatNPR, formatDate, getInitials } from '@/utils/format';
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, Input } from '@nepal-ecom/ui';

export function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendForm, setShowSuspendForm] = useState(false);

  const { data: user, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'customer', id],
    queryFn: async () => {
      const res = await adminApi.getUser(id!);
      return res.data.data as any;
    },
    enabled: !!id,
  });

  const suspendMutation = useMutation({
    mutationFn: () => adminApi.suspendUser(id!, suspendReason),
    onSuccess: () => {
      toast.success('Account suspended');
      setShowSuspendForm(false);
      setSuspendReason('');
      qc.invalidateQueries({ queryKey: ['admin', 'customer', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to suspend'),
  });

  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.reactivateUser(id!),
    onSuccess: () => {
      toast.success('Account reactivated');
      qc.invalidateQueries({ queryKey: ['admin', 'customer', id] });
      qc.invalidateQueries({ queryKey: ['admin', 'customers'] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to reactivate'),
  });

  if (isLoading) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-32 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading customer…
      </div>
    );
  }
  if (isError || !user) {
    return (
      <div className="animate-fade-in py-20 text-center">
        <AlertCircle className="h-10 w-10 mx-auto text-red-500 mb-3" />
        <p className="text-sm text-slate-600 mb-4">Could not load this customer.</p>
        <Button size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <Breadcrumb items={[{ label: 'Customers', to: '/users' }, { label: user.fullName || 'Customer' }]} />

      <button
        onClick={() => navigate('/users')}
        className="text-xs text-slate-500 hover:text-primary-600 flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="h-3 w-3" /> Back to customers
      </button>

      <Card className="mb-5">
        <CardContent className="p-6 flex items-start gap-5">
          <Avatar className="h-20 w-20">
            <AvatarFallback className="text-xl">{getInitials(user.fullName || user.email || '??')}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-slate-900">{user.fullName || '(no name)'}</h1>
              <Badge variant={user.isActive ? 'success' : 'destructive'}>
                {user.isActive ? 'Active' : 'Suspended'}
              </Badge>
              {user.isVerified && (
                <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Verified
                </span>
              )}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              {user.email && (
                <p className="flex items-center gap-2 text-slate-700">
                  <Mail className="h-3.5 w-3.5 text-slate-400" /> {user.email}
                </p>
              )}
              {user.phone && (
                <p className="flex items-center gap-2 text-slate-700">
                  <Phone className="h-3.5 w-3.5 text-slate-400" /> {user.phone}
                </p>
              )}
              <p className="flex items-center gap-2 text-slate-500 text-xs pt-1">
                <Calendar className="h-3 w-3" /> Joined {formatDate(user.createdAt)}
                {user.lastLoginAt && ` · last login ${formatDate(user.lastLoginAt)}`}
              </p>
              <p className="flex items-center gap-2 text-slate-500 text-xs">
                <Globe className="h-3 w-3" /> Preferred language: {user.preferredLanguage}
              </p>
            </div>
          </div>
          <div>
            {user.isActive ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSuspendForm((v) => !v)}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Ban className="h-4 w-4 mr-1" /> Suspend
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
              >
                {reactivateMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                <CheckCircle className="h-4 w-4 mr-1" /> Reactivate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {showSuspendForm && user.isActive && (
        <Card className="mb-5 border-red-200">
          <CardContent className="p-5">
            <p className="text-sm font-semibold text-red-800 mb-2">Suspend this account?</p>
            <p className="text-xs text-slate-600 mb-3">They won't be able to log in, place orders, or write reviews until reactivated.</p>
            <Input
              placeholder="Reason (visible to other admins only)"
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              className="mb-3"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="destructive"
                disabled={suspendMutation.isPending}
                onClick={() => suspendMutation.mutate()}
              >
                {suspendMutation.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
                Confirm suspension
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowSuspendForm(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide mb-2"><ShoppingBag className="h-3.5 w-3.5" /> Orders</div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{user.stats.orderCount}</p>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">Total spend</div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{formatNPR(user.stats.totalSpend)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide mb-2"><Award className="h-3.5 w-3.5" /> Loyalty points</div>
          <p className="text-2xl font-bold text-slate-900 tabular-nums">{user.loyaltyPoints}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800">Recent orders</h2>
          </div>
          {user.recentOrders?.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-slate-500">No orders yet</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {user.recentOrders?.map((o: any) => (
                <Link
                  key={o.id}
                  to={`/orders/${o.id}`}
                  className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900 tabular-nums">#{o.orderNumber}</p>
                    <p className="text-xs text-slate-500">{formatDate(o.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <OrderStatusPill status={o.status} />
                    <p className="text-sm font-bold text-slate-900 tabular-nums w-24 text-right">{formatNPR(o.total)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
